import { Controller } from '@hotwired/stimulus';
import { Turbo } from '@hotwired/turbo-rails';
import { getCsrfToken } from '../utils/dev';
/**
 * Description
 * -------
 *
 * Handles custom amount checkout.
 * Can be used on any element.
 * Can be used multiple times in the document.
 *
 * Targets
 * -------
 *
 * - fakeInput - (required) - fake input that displays the selected amount
 * - input     - (required) - hidden input
 * - cancel    - (optional) - cancel button.
 *
 * Values
 * -------
 *
 * - selectedAmount - (required) - selected amount
 * - updatePath     - (required) - path to update the amount
 * - exponent       - (required) - exponent of the selected amount
 *
 * Usage
 * -------
 *
 * <div data-controller="checkout-custom-amount">
 *   <input data-checkout-custom-amount-target="input" type="number" />
 *
 *   <input
 *     data-checkout-custom-amount-target="fakeInput"
 *     type="number"
 *     data-action="keydown->checkout-custom-amount#handleKeydown blur->checkout-custom-amount#handleBlur focusout->checkout-custom-amount#updateAmount"
 *  />
 *
 *   <button
 *     data-checkout-custom-amount-target="cancel"
 *     data-action="checkout-custom-amount#reset checkout-custom-amount#updateAmount"
 *   >
 *     Cancel
 *   </button>
 * </div>
 */
export default class default_1 extends Controller {
    static targets = ['fakeInput', 'input', 'cancel'];
    static values = {
        selectedAmount: Number,
        updatePath: String,
        exponent: Number,
    };
    handleKeydown(e) {
        if (['+', '-', 'E', 'e'].includes(e.key))
            e.preventDefault();
        if (e.key == 'Enter')
            e.preventDefault();
    }
    handleBlur(e) {
        if (!(e.target instanceof HTMLInputElement))
            return;
        const parsedValue = Number(parseFloat(e.target.value).toFixed(this.exponentValue));
        if (parsedValue > 0) {
            e.target.value = parsedValue.toString();
            this.inputTarget.value = parseInt((parsedValue * this.fractionDigits()).toFixed(this.exponentValue)).toString();
            if (parsedValue != Number(e.target.min)) {
                this.cancelTarget.classList.remove('hidden');
            }
            else {
                this.cancelTarget.classList.add('hidden');
            }
        }
        else {
            this.reset();
        }
    }
    reset() {
        this.cancelTarget.classList.add('hidden');
        this.inputTarget.value = this.selectedAmountValue.toString();
        this.fakeInputTarget.value = (this.selectedAmountValue / this.fractionDigits()).toFixed(this.exponentValue);
    }
    async updateAmount() {
        const response = await fetch(this.updatePathValue, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'text/vnd.turbo-stream.html',
                'X-CSRF-Token': getCsrfToken(),
            },
            body: JSON.stringify({ amount: this.inputTarget.value }),
        });
        if (response.ok) {
            response.text().then(Turbo.renderStreamMessage);
        }
    }
    fractionDigits() {
        return Math.pow(10, this.exponentValue);
    }
}
